import type { useAppDispatch } from '../grid/reduxStore/Store';
import { rootStore } from '../grid/reduxStore/Store';
import type { ErrorCode } from '../../../services/socket/SocketEvents';
import { AcknowledgmentResponseStatus } from '../../../services/socket/SocketEvents';
import { addSection, deleteSection, selectSectionById, addGridBlockState } from '../grid/reduxStore/editorSlice';
import { DEFAULT_BLOCK_BORDER_COLOR } from '../shared/documentColorDefaults';
import { addBlockSettings } from '../grid/reduxStore/blockStyleSettingsSlice';
import type { BlockSettings } from '../grid/reduxStore/blockStyleSettingsSlice';
import { setSaveStatus, setSaveStatusBasedOnApiResponse } from '../grid/reduxStore/saveStatusSlice';
import { DocumentSaveStatus } from '../shared/models/DocumentSaveStatus';
import type { UndoRedoCommandInterface } from './undoRedoCommandVersion';
import { serializeBlockContent } from './serializeBlockContent';

import { GridBlockType } from 'services/entities/BlockTypes';
import {
  addSectionViaSocket,
  deleteSectionViaSocket,
  addBlockViaSocket,
  addSignatureViaSocket,
  buildBlockAddedPayload,
  buildBlockTableAddedPayload,
} from '../grid/reduxStore/saveHandlers';
import type { SignatureBox } from 'services/entities/SignatureTypes';
import { selectBlockStyleById } from '../grid/reduxStore/blockStyleSettingsSlice';

export interface DeleteSectionCommandDependencies {
  dispatch: ReturnType<typeof useAppDispatch>;
  socketClient: any;
  userId: number;
  checkDocumentLockStatus: (errorCode: ErrorCode) => boolean;
}

interface BlockSnapshot {
  blockId: string;
  type: GridBlockType;
  content: any;
  contentTable?: any;
  contentSignature?: SignatureBox;
  blockConfig: {
    id: string;
    x: number;
    y: number;
    width: number;
    height: number;
    z: number;
  };
  styles?: BlockSettings;
}

export interface DeleteSectionCommandParams {
  sectionId: string;
  afterSectionId: string | null;
}

export class DeleteSectionCommand implements UndoRedoCommandInterface {
  readonly scope = 'section' as const;
  readonly timestamp: string;
  readonly sectionId: string;

  private readonly dispatch: ReturnType<typeof useAppDispatch>;
  private readonly socketClient: any;
  private readonly userId: number;
  private readonly checkDocumentLockStatus: (errorCode: ErrorCode) => boolean;

  private readonly afterSectionId: string | null;
  private readonly sectionTitle: string;
  private readonly blockSnapshots: BlockSnapshot[];

  constructor(dependencies: DeleteSectionCommandDependencies, params: DeleteSectionCommandParams) {
    this.timestamp = new Date().toISOString();
    this.dispatch = dependencies.dispatch;
    this.socketClient = dependencies.socketClient;
    this.userId = dependencies.userId;
    this.checkDocumentLockStatus = dependencies.checkDocumentLockStatus;
    this.sectionId = params.sectionId;
    this.afterSectionId = params.afterSectionId;

    const state = rootStore.getState();
    const section = selectSectionById(state, params.sectionId);

    if (!section) {
      throw new Error(`DeleteSectionCommand: section ${params.sectionId} not found`);
    }

    this.sectionTitle = section.title;

    this.blockSnapshots = Object.entries(section.blocksContent).map(([blockId, block]) => {
      const styles = block.type !== GridBlockType.SIGNATURE ? selectBlockStyleById(state, blockId) || undefined : undefined;

      return {
        blockId,
        type: block.type,
        content: block.content,
        contentTable: block.contentTable,
        contentSignature: block.contentSignature,
        blockConfig: { ...block.blockConfig },
        styles: styles ? { ...styles } : undefined,
      };
    });
  }

  async execute(): Promise<boolean> {
    return this.deleteSection();
  }

  async undo(): Promise<boolean> {
    return this.restoreSectionWithBlocks();
  }

  async redo(): Promise<boolean> {
    return this.deleteSection();
  }

  getCurrentSectionId(): string {
    return this.sectionId;
  }

  private async deleteSection(): Promise<boolean> {
    try {
      this.dispatch(setSaveStatus({ status: DocumentSaveStatus.SAVING }));

      const response = await deleteSectionViaSocket(this.socketClient, { sectionId: this.sectionId });

      if (this.checkDocumentLockStatus(response.errorCode)) {
        this.dispatch(setSaveStatus({ status: DocumentSaveStatus.NOT_SAVED }));
        return false;
      }

      if (response.status !== AcknowledgmentResponseStatus.OK) {
        this.dispatch(setSaveStatus({ status: DocumentSaveStatus.NOT_SAVED }));
        return false;
      }

      this.dispatch(deleteSection({ sectionId: this.sectionId }));
      this.dispatch(setSaveStatusBasedOnApiResponse({ status: response.status, errorCode: response.errorCode }));

      return true;
    } catch (error) {
      console.error('DeleteSectionCommand: delete failed', error);
      this.dispatch(setSaveStatus({ status: DocumentSaveStatus.NOT_SAVED }));
      return false;
    }
  }

  private async restoreSectionWithBlocks(): Promise<boolean> {
    try {
      this.dispatch(setSaveStatus({ status: DocumentSaveStatus.SAVING }));

      const sectionResponse = await addSectionViaSocket(this.socketClient, {
        id: this.sectionId,
        title: this.sectionTitle,
        ...(this.afterSectionId !== null ? { afterSectionId: this.afterSectionId } : {}),
      });

      if (this.checkDocumentLockStatus(sectionResponse.errorCode)) {
        this.dispatch(setSaveStatus({ status: DocumentSaveStatus.NOT_SAVED }));
        return false;
      }

      if (sectionResponse.status !== AcknowledgmentResponseStatus.OK) {
        this.dispatch(setSaveStatus({ status: DocumentSaveStatus.NOT_SAVED }));
        return false;
      }

      this.dispatch(
        addSection({
          sectionId: this.sectionId,
          title: this.sectionTitle,
          afterSectionId: this.afterSectionId,
        })
      );

      for (const snapshot of this.blockSnapshots) {
        const success = await this.restoreBlock(snapshot);
        if (!success) {
          console.error(`DeleteSectionCommand: failed to restore block ${snapshot.blockId}`);
        }
      }

      this.dispatch(
        setSaveStatusBasedOnApiResponse({
          status: sectionResponse.status,
          errorCode: sectionResponse.errorCode,
        })
      );

      return true;
    } catch (error) {
      console.error('DeleteSectionCommand: undo failed', error);
      this.dispatch(setSaveStatus({ status: DocumentSaveStatus.NOT_SAVED }));
      return false;
    }
  }

  private async restoreBlock(snapshot: BlockSnapshot): Promise<boolean> {
    try {
      switch (snapshot.type) {
        case GridBlockType.SIGNATURE:
          return await this.restoreSignatureBlock(snapshot);
        case GridBlockType.TABLE:
          return await this.restoreTableBlock(snapshot);
        case GridBlockType.TEXT:
        case GridBlockType.IMAGE:
        case GridBlockType.VIDEO:
        case GridBlockType.SHAPE:
          return await this.restoreRegularBlock(snapshot);
        default:
          console.warn(`DeleteSectionCommand: unknown block type ${snapshot.type}`);
          return false;
      }
    } catch (error) {
      console.error(`DeleteSectionCommand: restoreBlock failed for ${snapshot.blockId}`, error);
      return false;
    }
  }

  private async restoreSignatureBlock(snapshot: BlockSnapshot): Promise<boolean> {
    if (!snapshot.contentSignature) return false;

    const response = await addSignatureViaSocket(this.socketClient, this.sectionId, {
      ...snapshot.contentSignature,
      signatureBoxId: snapshot.blockId,
    });

    if (response.status !== AcknowledgmentResponseStatus.OK) return false;

    // The backend generates a fresh id server-side on every add_signature call, so the Redux
    // entry must be keyed by that id, not the deleted signature's original one.
    const persistedId = response.content.id;
    const signature: SignatureBox = {
      ...snapshot.contentSignature,
      signatureBoxId: persistedId,
    };

    this.dispatch(
      addGridBlockState({
        sectionId: this.sectionId,
        blockId: persistedId,
        content: signature,
        blockConfig: { ...snapshot.blockConfig, id: persistedId },
        blockType: GridBlockType.SIGNATURE,
      })
    );

    return true;
  }

  private async restoreTableBlock(snapshot: BlockSnapshot): Promise<boolean> {
    if (!snapshot.contentTable) return false;

    const payload = buildBlockTableAddedPayload({
      blockId: snapshot.blockId,
      userId: this.userId,
      content: snapshot.contentTable,
      position: { top_px: snapshot.blockConfig.y, left_px: snapshot.blockConfig.x },
      dimensions: { width_px: snapshot.blockConfig.width, height_px: snapshot.blockConfig.height },
      sectionId: this.sectionId,
      zIndex: snapshot.blockConfig.z,
    });

    const response = await addBlockViaSocket(this.socketClient, payload);

    if (this.checkDocumentLockStatus(response.errorCode)) return false;
    if (response.status !== AcknowledgmentResponseStatus.OK) return false;

    this.dispatch(
      addGridBlockState({
        sectionId: this.sectionId,
        blockId: snapshot.blockId,
        content: snapshot.contentTable,
        blockConfig: { ...snapshot.blockConfig, id: snapshot.blockId },
        blockType: GridBlockType.TABLE,
      })
    );

    return true;
  }

  private async restoreRegularBlock(snapshot: BlockSnapshot): Promise<boolean> {
    const htmlContent = serializeBlockContent(snapshot.content);

    const payload = buildBlockAddedPayload({
      blockId: snapshot.blockId,
      userId: this.userId,
      type: snapshot.type as GridBlockType.TEXT | GridBlockType.IMAGE | GridBlockType.VIDEO | GridBlockType.SHAPE,
      htmlContent,
      position: { top_px: snapshot.blockConfig.y, left_px: snapshot.blockConfig.x },
      dimensions: { width_px: snapshot.blockConfig.width, height_px: snapshot.blockConfig.height },
      sectionId: this.sectionId,
      zIndex: snapshot.blockConfig.z,
      ...(snapshot.styles && {
        styles: this.cleanStylesForPayload(snapshot.styles, snapshot.blockId),
      }),
    });

    const response = await addBlockViaSocket(this.socketClient, payload);

    if (this.checkDocumentLockStatus(response.errorCode)) return false;
    if (response.status !== AcknowledgmentResponseStatus.OK) return false;

    this.dispatch(
      addGridBlockState({
        sectionId: this.sectionId,
        blockId: snapshot.blockId,
        content: snapshot.content || '',
        blockConfig: { ...snapshot.blockConfig, id: snapshot.blockId },
        blockType: snapshot.type,
      })
    );

    if (snapshot.styles) {
      this.dispatch(
        addBlockSettings({
          blockId: snapshot.blockId,
          blockSettings: {
            ...snapshot.styles,
            blockId: snapshot.blockId,
            updatedByUserId: this.userId,
            currentTimestamp: new Date().toISOString(),
          },
        })
      );
    }

    return true;
  }

  private cleanStylesForPayload(styles: BlockSettings, blockId: string): any {
    return {
      blockId,
      updatedByUserId: this.userId,
      currentTimestamp: new Date().toISOString(),
      borderLeft: styles.borderLeft ?? 0,
      borderRight: styles.borderRight ?? 0,
      borderTop: styles.borderTop ?? 0,
      borderBottom: styles.borderBottom ?? 0,
      borderRadius: styles.borderRadius ?? 0,
      paddingLeft: styles.paddingLeft ?? 0,
      paddingRight: styles.paddingRight ?? 0,
      paddingTop: styles.paddingTop ?? 0,
      paddingBottom: styles.paddingBottom ?? 0,
      borderColor: styles.borderColor ?? DEFAULT_BLOCK_BORDER_COLOR,
      ...(styles.backgroundColor !== undefined && { backgroundColor: styles.backgroundColor }),
      ...(styles.opacity !== undefined && { opacity: styles.opacity }),
      ...(styles.imageAlt !== undefined && { imageAlt: styles.imageAlt }),
      ...(styles.imageLink !== undefined && { imageLink: styles.imageLink }),
      ...(styles.imageAspectRatioLock !== undefined && { imageAspectRatioLock: styles.imageAspectRatioLock }),
      ...(styles.lockedBy !== undefined && { lockedBy: styles.lockedBy }),
    };
  }

  toJSON(): Record<string, any> {
    return {
      sectionId: this.sectionId,
      title: this.sectionTitle,
      afterSectionId: this.afterSectionId,
      blockCount: this.blockSnapshots.length,
    };
  }
}
